import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Users,
  Sparkles,
  Award,
  TrendingUp,
  Calendar,
  BarChart3,
  Megaphone,
  MessageSquare,
  Link as LinkIcon,
  Activity,
  CheckCircle2,
  Info,
  Video,
  Share2,
  Gamepad2,
  ChevronRight,
} from "lucide-react";
import { PageLayout } from "../components/PageLayout";

const PROFILE_REQUIREMENTS = [
  {
    icon: Video,
    emoji: "🎥",
    title: "Streamers",
    requirements: [
      "Tener más de 25 viewers constantes en tus streams.",
      "Al menos 1,000 seguidores en Twitch o plataformas similares.",
    ],
  },
  {
    icon: Share2,
    emoji: "📱",
    title: "Creadores de Contenido",
    requirements: [
      "Tener más de 1,000 seguidores en tus redes sociales.",
      "Ser constante en la creación de contenido relacionado con Minecraft o gaming.",
    ],
  },
  {
    icon: Gamepad2,
    emoji: "🎮",
    title: "Estudios / Eventos",
    requirements: [
      "Tener más de 50 jugadores en tus eventos.",
      "Contar con más de 300 miembros activos en tu comunidad de Discord.",
    ],
  },
];

const RESPONSIBILITIES = [
  {
    icon: Megaphone,
    emoji: "📢",
    title: "Visibilidad Mensual",
    description: "Menciona a OptiShield X en posts, mensajes, descripciones de videos y redes sociales.",
  },
  {
    icon: MessageSquare,
    emoji: "🪧",
    title: "Mensajes en Servidor",
    description: "TAB, scoreboard, hologramas y mensajes de bienvenida mencionando a OptiShield X.",
  },
  {
    icon: LinkIcon,
    emoji: "🔗",
    title: "Difusión Constante",
    description: "Comparte tu cupón de descuento y enlace de invitación regularmente con tu comunidad.",
  },
  {
    icon: Activity,
    emoji: "📈",
    title: "Mantener Actividad",
    description: "Eventos, contenido constante, servidor activo o crecimiento de tu network.",
  },
];

const EVALUATION_STEPS = [
  {
    icon: Calendar,
    emoji: "🗓️",
    title: "30 Días de Prueba",
    description: "Cada afiliación inicia con 1 mes completo de prueba para que puedas demostrar tu valor y adaptarte al programa.",
  },
  {
    icon: BarChart3,
    emoji: "🔄",
    title: "Evaluación Automática",
    description: "Sistema automatizado escanea tu rentabilidad cada 15 días analizando tráfico, ventas y actividad generada.",
  },
  {
    icon: TrendingUp,
    emoji: "📊",
    title: "Recursos Dinámicos",
    description: "Tus recursos pueden subir, mantenerse o reducirse según tu rendimiento y conversiones generadas.",
  },
  {
    icon: Sparkles,
    emoji: "💡",
    title: "Solicita lo que Necesites",
    description: "Libre acceso para solicitar estrategias, modificar recursos o pedir lo que necesites para crecer.",
  },
];

const TIERS = [
  {
    name: "BRONCE",
    ram: "8 GB Base",
    boostTo: "12 GB",
    boostsPerMonth: 1,
    servers: "1 servidor",
    features: ["Código de descuento personalizado", "Visibilidad mensual mínima"],
    gradient: "linear-gradient(135deg, #cd7f32, #b87333)",
  },
  {
    name: "ORO",
    ram: "12 GB Base",
    boostTo: "16 GB",
    boostsPerMonth: 2,
    servers: "1 servidor intermedio",
    features: ["Prioridad en soporte", "Mayores beneficios", "Rentabilidad mensual requerida"],
    gradient: "linear-gradient(135deg, #ffd700, #ffed4e)",
    popular: true,
  },
  {
    name: "DIAMANTE",
    ram: "16 GB Base",
    boostTo: "24 GB",
    boostsPerMonth: 3,
    servers: "1 servidor avanzado",
    features: ["Prioridad máxima", "Mayores beneficios", "Alta rentabilidad mensual"],
    gradient: "linear-gradient(135deg, #b9f2ff, #00d4ff)",
  },
];

export const AfiliadosPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    link: "",
    type: "",
    creatorSince: "",
    studioSince: "",
    eventDescription: "",
    accepted: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.accepted) {
      alert("Debes aceptar las responsabilidades antes de continuar.");
      return;
    }
    // Form submission would be handled here in production
    alert("Tu solicitud ha sido enviada. Te contactaremos pronto.");
  };

  const isCreatorOrStreamer = formData.type === "Streamer" || formData.type === "Creador de Contenido";
  const isStudioOrEvent = formData.type === "Estudio / Eventos";

  return (
    <PageLayout title="Afiliados | OptiShield X" showBackgroundOrbs>
      {/* Hero Section */}
      <section className="relative osx-noise" data-testid="affiliates-hero">
        <div className="absolute inset-0 osx-grid-bg opacity-50" aria-hidden />
        <div className="osx-container relative pt-14 sm:pt-20 pb-10">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5"
            style={{
              background: "var(--osx-surface-2)",
              border: "1px solid var(--osx-border-strong)",
              color: "var(--osx-brand-600)",
            }}
          >
            <Users size={13} /> Programa de Afiliados
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
            style={{ color: "var(--osx-text)" }}
            data-testid="affiliates-headline"
          >
            <span className="osx-gradient-text">Afiliados y Colaboraciones</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mt-4 max-w-2xl text-base sm:text-lg"
            style={{ color: "var(--osx-text-muted)" }}
          >
            Únete a nuestro programa de afiliados y crece junto a OptiShield X. Obtén recursos gratuitos, descuentos exclusivos y beneficios únicos.
          </motion.p>
        </div>
      </section>

      {/* Profile Requirements */}
      <section className="relative pb-12 pt-8">
        <div className="osx-container">
          <h2 className="text-3xl font-bold tracking-tight mb-8 text-center" style={{ color: "var(--osx-text)" }}>
            ¿Qué perfil buscas?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {PROFILE_REQUIREMENTS.map((profile, idx) => (
              <motion.div
                key={profile.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * idx }}
                className="p-6 rounded-2xl"
                style={{
                  background: "var(--osx-surface)",
                  border: "1px solid var(--osx-border)",
                  boxShadow: "var(--osx-shadow-1)",
                }}
                data-testid={`profile-${profile.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div
                  className="inline-flex h-14 w-14 items-center justify-center rounded-2xl mb-4 text-2xl"
                  style={{
                    background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                  }}
                >
                  {profile.emoji}
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: "var(--osx-text)" }}>
                  {profile.title}
                </h3>
                <ul className="space-y-2">
                  {profile.requirements.map((req) => (
                    <li key={req} className="flex items-start gap-2 text-sm" style={{ color: "var(--osx-text-muted)" }}>
                      <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: "var(--osx-brand-600)" }} />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Responsibilities */}
      <section className="relative pb-12">
        <div className="osx-container">
          <div
            className="rounded-3xl p-8 sm:p-12"
            style={{
              background: "var(--osx-surface)",
              border: "1px solid var(--osx-border)",
              boxShadow: "var(--osx-shadow-2)",
            }}
          >
            <div className="flex items-start gap-3 mb-6">
              <span
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-xl shrink-0"
                style={{ background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))" }}
              >
                🎯
              </span>
              <div>
                <h2 className="text-2xl font-bold" style={{ color: "var(--osx-text)" }}>
                  Tus responsabilidades
                </h2>
                <p className="text-sm mt-1" style={{ color: "var(--osx-text-muted)" }}>
                  Lee esto antes de solicitar tu afiliación
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {RESPONSIBILITIES.map((resp) => (
                <div
                  key={resp.title}
                  className="p-4 rounded-xl flex items-start gap-3"
                  style={{
                    background: "var(--osx-surface-2)",
                    border: "1px solid var(--osx-border)",
                  }}
                  data-testid={`responsibility-${resp.title.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <span className="text-2xl shrink-0">{resp.emoji}</span>
                  <div>
                    <h4 className="text-sm font-bold mb-1" style={{ color: "var(--osx-text)" }}>
                      {resp.title}
                    </h4>
                    <p className="text-xs leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
                      {resp.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div
              className="mt-6 p-4 rounded-xl flex items-start gap-3"
              style={{
                background: "color-mix(in oklab, var(--osx-brand-600) 8%, var(--osx-surface))",
                border: "1px solid var(--osx-border-strong)",
              }}
            >
              <TrendingUp size={20} className="shrink-0" style={{ color: "var(--osx-brand-600)" }} />
              <div>
                <h4 className="text-sm font-bold mb-1" style={{ color: "var(--osx-text)" }}>
                  📈 Tier y Permanencia
                </h4>
                <p className="text-xs leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
                  Tu tier y permanencia dependen del tráfico y uso de cupón que generes. El nivel de recursos asignado y tu tiempo en el programa están determinados por las conversiones mensuales que aportes a OptiShield X. Mayor actividad = mejor tier.
                </p>
              </div>
            </div>

            <div
              className="mt-4 p-4 rounded-xl flex items-start gap-3"
              style={{
                background: "color-mix(in oklab, #3b82f6 8%, var(--osx-surface))",
                border: "1px solid rgba(59, 130, 246, 0.3)",
              }}
            >
              <Info size={20} className="shrink-0" style={{ color: "#3b82f6" }} />
              <div>
                <h4 className="text-sm font-bold mb-1" style={{ color: "var(--osx-text)" }}>
                  ℹ️ Importante
                </h4>
                <p className="text-xs leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
                  Este programa está diseñado para creadores que demuestran <strong style={{ color: "var(--osx-text)" }}>valor real y conversiones constantes</strong>. Si cumples con los requisitos y mantienes actividad mensual medible, tu afiliación permanecerá activa y podrás progresar en tiers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Evaluation System */}
      <section className="relative pb-12">
        <div className="osx-container">
          <div className="text-center mb-10">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-4"
              style={{
                background: "var(--osx-surface-2)",
                border: "1px solid var(--osx-border-strong)",
                color: "var(--osx-brand-600)",
              }}
            >
              <BarChart3 size={13} /> 📈 Sistema Automatizado
            </div>
            <h2 className="text-3xl font-bold tracking-tight" style={{ color: "var(--osx-text)" }}>
              Sistema de Evaluación
            </h2>
            <p className="mt-2 text-sm" style={{ color: "var(--osx-text-muted)" }}>
              Evaluación automática cada 15 días para optimizar tu experiencia
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {EVALUATION_STEPS.map((step, idx) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * idx }}
                className="p-5 rounded-2xl text-center"
                style={{
                  background: "var(--osx-surface)",
                  border: "1px solid var(--osx-border)",
                  boxShadow: "var(--osx-shadow-1)",
                }}
                data-testid={`evaluation-${step.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="text-3xl mb-3">{step.emoji}</div>
                <h4 className="text-sm font-bold mb-2" style={{ color: "var(--osx-text)" }}>
                  {step.title}
                </h4>
                <p className="text-xs leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="relative pb-12">
        <div className="osx-container">
          <h2 className="text-3xl font-bold tracking-tight mb-8 text-center" style={{ color: "var(--osx-text)" }}>
            Niveles de Afiliación
          </h2>
          <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {TIERS.map((tier) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="relative p-6 rounded-2xl overflow-hidden"
                style={{
                  background: "var(--osx-surface)",
                  border: tier.popular ? "2px solid var(--osx-brand-600)" : "1px solid var(--osx-border)",
                  boxShadow: tier.popular ? "0 12px 32px rgba(2,132,199,0.25)" : "var(--osx-shadow-1)",
                  transform: tier.popular ? "scale(1.05)" : "scale(1)",
                }}
                data-testid={`tier-${tier.name.toLowerCase()}`}
              >
                {tier.popular && (
                  <div
                    className="absolute top-0 left-0 right-0 py-1.5 text-center text-xs font-bold text-white"
                    style={{ background: tier.gradient }}
                  >
                    MÁS POPULAR
                  </div>
                )}
                <div className={tier.popular ? "mt-7" : ""}>
                  <h3
                    className="text-2xl font-bold mb-1"
                    style={{
                      background: tier.gradient,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    {tier.name}
                  </h3>
                  <p className="text-lg font-semibold mb-4" style={{ color: "var(--osx-text)" }}>
                    {tier.ram}
                  </p>

                  <div
                    className="p-3 rounded-xl mb-4"
                    style={{
                      background: "var(--osx-surface-2)",
                      border: "1px solid var(--osx-border)",
                    }}
                  >
                    <div className="text-xs uppercase tracking-wider mb-2" style={{ color: "var(--osx-text-muted)" }}>
                      POTENCIA DEL SERVIDOR
                    </div>
                    <div className="space-y-1 text-sm" style={{ color: "var(--osx-text)" }}>
                      <div>• Boost hasta <strong>{tier.boostTo}</strong></div>
                      <div>• <strong>{tier.boostsPerMonth}</strong> boosts / mes</div>
                      <div>• <strong>{tier.servers}</strong></div>
                    </div>
                  </div>

                  <ul className="space-y-2">
                    {tier.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-2 text-sm" style={{ color: "var(--osx-text-muted)" }}>
                        <ChevronRight size={16} className="shrink-0 mt-0.5" style={{ color: "var(--osx-brand-600)" }} />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="relative pb-20">
        <div className="osx-container max-w-3xl">
          <div
            className="rounded-3xl p-8 sm:p-12"
            style={{
              background: "var(--osx-surface)",
              border: "1px solid var(--osx-border)",
              boxShadow: "var(--osx-shadow-2)",
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Award size={28} style={{ color: "var(--osx-brand-600)" }} />
              <h2 className="text-2xl font-bold" style={{ color: "var(--osx-text)" }}>
                Formulario de Postulación
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="affiliate-application-form">
              {/* Acceptance Checkbox */}
              <label
                className="flex items-start gap-3 p-4 rounded-xl cursor-pointer"
                style={{
                  background: "var(--osx-surface-2)",
                  border: "1px solid var(--osx-border)",
                }}
              >
                <input
                  type="checkbox"
                  name="accepted"
                  checked={formData.accepted}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  data-testid="acceptance-checkbox"
                />
                <span className="text-sm" style={{ color: "var(--osx-text-muted)" }}>
                  He leído y acepto mis responsabilidades y entiendo el sistema de evaluación automatizado de OptiShield X.
                </span>
              </label>

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: "var(--osx-text)" }}>
                  Nombre Completo (o Nombre Representativo):
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Juan Pérez"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{
                    background: "var(--osx-surface-2)",
                    border: "1px solid var(--osx-border)",
                    color: "var(--osx-text)",
                  }}
                  data-testid="input-name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: "var(--osx-text)" }}>
                  Correo Electrónico (para enviarte la respuesta):
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="ejemplo@correo.com"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{
                    background: "var(--osx-surface-2)",
                    border: "1px solid var(--osx-border)",
                    color: "var(--osx-text)",
                  }}
                  data-testid="input-email"
                />
              </div>

              {/* Link */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: "var(--osx-text)" }}>
                  Enlace principal de tu proyecto (Twitch, YouTube, Discord, Twitter):
                </label>
                <input
                  type="url"
                  name="link"
                  value={formData.link}
                  onChange={handleChange}
                  required
                  placeholder="https://twitch.tv/tunombre"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{
                    background: "var(--osx-surface-2)",
                    border: "1px solid var(--osx-border)",
                    color: "var(--osx-text)",
                  }}
                  data-testid="input-link"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: "var(--osx-text)" }}>
                  ¿Qué tipo de afiliación buscas?
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{
                    background: "var(--osx-surface-2)",
                    border: "1px solid var(--osx-border)",
                    color: "var(--osx-text)",
                  }}
                  data-testid="input-type"
                >
                  <option value="">Selecciona una opción...</option>
                  <option value="Streamer">Streamer</option>
                  <option value="Creador de Contenido">Creador de Contenido</option>
                  <option value="Estudio / Eventos">Estudio / Eventos</option>
                </select>
              </div>

              {/* Conditional: Creator/Streamer */}
              {isCreatorOrStreamer && (
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: "var(--osx-text)" }}>
                    ¿Desde cuándo eres creador de contenido o streamer?
                  </label>
                  <input
                    type="text"
                    name="creatorSince"
                    value={formData.creatorSince}
                    onChange={handleChange}
                    required
                    placeholder="Ej: Desde 2020"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                    style={{
                      background: "var(--osx-surface-2)",
                      border: "1px solid var(--osx-border)",
                      color: "var(--osx-text)",
                    }}
                    data-testid="input-creator-since"
                  />
                </div>
              )}

              {/* Conditional: Studio/Events */}
              {isStudioOrEvent && (
                <>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: "var(--osx-text)" }}>
                      ¿Desde cuándo tienes tu estudio u organizas eventos?
                    </label>
                    <input
                      type="text"
                      name="studioSince"
                      value={formData.studioSince}
                      onChange={handleChange}
                      required
                      placeholder="Ej: Desde 2021"
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                      style={{
                        background: "var(--osx-surface-2)",
                        border: "1px solid var(--osx-border)",
                        color: "var(--osx-text)",
                      }}
                      data-testid="input-studio-since"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: "var(--osx-text)" }}>
                      Cuéntanos detalladamente de qué tratan tus eventos o tu servidor:
                    </label>
                    <textarea
                      name="eventDescription"
                      value={formData.eventDescription}
                      onChange={handleChange}
                      required
                      rows={4}
                      placeholder="Describe tu proyecto..."
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none"
                      style={{
                        background: "var(--osx-surface-2)",
                        border: "1px solid var(--osx-border)",
                        color: "var(--osx-text)",
                      }}
                      data-testid="input-event-description"
                    />
                  </div>
                </>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full px-6 py-3.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                  boxShadow: "0 8px 22px rgba(2,132,199,0.28)",
                  transition: "transform 0.2s ease",
                }}
                data-testid="submit-affiliate-form"
              >
                <Sparkles size={16} />
                Enviar Solicitud de Afiliación
              </button>
            </form>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};
